import React from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'motion/react';
import { ReviewWriteForm } from './ReviewWriteForm';
import { getDefaultConfirmationData } from '../utils/modalUtils';
import { ModalOverlay } from './common/SharedStyles';
import { CompleteView } from './common/CompleteView';
import { ConfirmationDialog } from './common/ConfirmationDialog';
import { COLORS, FONT_SIZES, FONT_WEIGHTS, BORDER_RADIUS, SPACING } from './common/constants';
import { useReviewForm } from '../hooks/useReviewForm';
import { useReviewModalState } from '../hooks/useReviewModalState';
import { useReviewSubmission } from '../hooks/useReviewSubmission';
import type { ReviewModalProps } from './common/interfaces';

const Modal = styled(motion.div)`
  background: ${COLORS.background};
  border-radius: ${BORDER_RADIUS.lg};
  width: 686px;
  max-width: 90vw;
  max-height: 90vh;
  box-shadow: 0 8px 32px ${COLORS.shadow};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SPACING.xl} ${SPACING.xl} 0px ${SPACING.xl};
`;

const Title = styled.div`
  font-size: ${FONT_SIZES.xl};
  font-weight: ${FONT_WEIGHTS.normal};
  color: ${COLORS.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${FONT_SIZES['2xl']} !important;
  font-weight: ${FONT_WEIGHTS.normal};
  color: #333;
  cursor: pointer;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${SPACING.lg} ${SPACING.xl} ${SPACING.xl} ${SPACING.xl};
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xl};
`;

export const ReviewWritePcModal = ({ isOpen, productImage, onClose, onSubmit }: ReviewModalProps) => {
  const { formState, formActions, formOptions, hasUnsavedChanges, isSubmitDisabled, resetForm } = useReviewForm({
    isOpen,
  });

  const { isComplete, showConfirmation, setIsComplete, handleClose, handleConfirmClose, handleCancelClose } =
    useReviewModalState({
      isOpen,
      onClose: () => {
        resetForm();
        onClose();
      },
      hasUnsavedChanges,
      enableConfirmation: true, // PC modal uses confirmation dialog
    });

  const { handleSubmit } = useReviewSubmission({
    formData: formState,
    onSubmit,
    onSubmissionComplete: () => setIsComplete(true),
  });

  const confirmationData = getDefaultConfirmationData();

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={handleClose}>
            <Modal
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 500 }}
              onClick={(e) => e.stopPropagation()}
            >
              {!isComplete && (
                <Header>
                  <Title>Review this product</Title>
                  <CloseButton onClick={handleClose}>×</CloseButton>
                </Header>
              )}
              <Content>
                {!isComplete ? (
                  <ReviewWriteForm
                    formData={formState}
                    formActions={formActions}
                    options={formOptions}
                    productImage={productImage}
                    isSubmitDisabled={isSubmitDisabled}
                    onSubmit={handleSubmit}
                    variant="pc"
                  />
                ) : (
                  <CompleteView onContinue={onClose} variant="pc" />
                )}
              </Content>
            </Modal>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        title={confirmationData.title}
        message={confirmationData.message}
        confirmText={confirmationData.confirmText}
        cancelText={confirmationData.cancelText}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
};
